import moment from "moment";
import { dateToView } from "../../../utils/date.utils";
import { stallLabel } from "../../../utils/stall.utils";

/**
 * helper ที่หน้าหลัก (carousel) กับหน้ารายละเอียดใช้ร่วมกัน
 * เดิม logic พวกนี้ถูก copy ไว้ทั้งใน home.tsx, detailInfo.tsx และ announce.tsx
 */

export type AnnounceMetaItem = {
  label: string;
  value: string;
  /** true = แสดงเป็นสีธีมพร้อมไอคอนปฏิทิน */
  isDate?: boolean;
};

/**
 * ฟอร์ม "ไม่กำหนดเวลา" บันทึก endDate เป็น +100 ปี
 * ทั้งระบบจึงเช็คด้วยส่วนต่าง > 50 ปี ว่าเป็นประกาศแบบไม่กำหนดวันสิ้นสุด
 */
export const isNoEndDate = (startDate?: any, endDate?: any) => {
  if (!startDate || !endDate) return false;
  return moment(endDate).diff(moment(startDate), "year") > 50;
};

/** ประกาศเช่าแผงค้าเก็บช่วงเวลาไว้ทั้งที่ตัวประกาศและใน round — ใช้ตัวประกาศก่อน */
const announceDateRange = (announce: any) => {
  const startDate = announce?.startDate || announce?.round?.[0]?.startDate;
  const endDate = announce?.endDate || announce?.round?.[0]?.endDate;
  return { startDate, endDate };
};

/** ช่วงเวลาประกาศในรูปแบบที่เอาไปแสดงได้เลย */
export const announceDateRangeToView = (announce: any) => {
  const { startDate, endDate } = announceDateRange(announce);
  if (!startDate) return "-";
  const end = isNoEndDate(startDate, endDate)
    ? "ไม่กำหนด"
    : endDate
    ? dateToView(endDate)
    : "ไม่กำหนด";
  return `${dateToView(startDate)} – ${end}`;
};

/** รวมค่าซ้ำใน round ให้เหลือชุดเดียว เช่น ประเภทแผงค้า / โซน */
const uniqueJoin = (values: any[]) => {
  const list = Array.from(new Set((values || []).filter((v: any) => v))) as string[];
  return list.length ? list.join(", ") : "-";
};

/** ข้อความในป้ายประเภทประกาศ — ยึดชื่อจาก master ก่อน ค่อย fallback ตาม announceType */
export const announceChipLabel = (announce: any) => {
  if (announce?.newsType?.newstypeNameTh) return announce.newsType.newstypeNameTh;
  if (announce?.announceType === "RENT") return "ประกาศเช่าแผงค้า";
  if (announce?.announceType === "HELP") return "ประกาศมาตรการช่วยเหลือ";
  return "ประกาศข่าวสาร";
};

/** แผงค้าทั้งหมดในประกาศ — ส่ง dash ว่างเพราะ uniqueJoin กรองค่าว่างทิ้งให้อยู่แล้ว */
export const announceStallNames = (announce: any) =>
  uniqueJoin((announce?.round || []).map((e: any) => stallLabel(e?.stall, "")));

/**
 * รายการข้อมูลย่อใต้หัวข้อประกาศ
 * @param rentDateLabel ป้ายช่วงเวลาของประกาศเช่าแผงค้า — หน้าหลักกับหน้ารายละเอียดใช้คำต่างกัน
 *                      ประกาศประเภทอื่นใช้ "ช่วงเวลาประกาศ" เสมอ
 */
export const getAnnounceMeta = (
  announce: any,
  rentDateLabel: string
): AnnounceMetaItem[] => {
  const isRent = announce?.announceType === "RENT";
  const dateItem: AnnounceMetaItem = {
    label: isRent ? rentDateLabel : "ช่วงเวลาประกาศ",
    value: announceDateRangeToView(announce),
    isDate: true,
  };

  if (!isRent) {
    return [
      {
        label: "ประเภทข่าว",
        value: announce?.newsType?.newstypeNameTh || "-",
      },
      dateItem,
    ];
  }

  const round: any[] = announce?.round || [];
  const zones = uniqueJoin(round.map((e: any) => e?.stall?.zone?.zoneName));

  return [
    { label: "จำนวนแผงค้า", value: `${round.length}` },
    {
      label: "ประเภทแผงค้า",
      value: uniqueJoin(round.map((e: any) => e?.stall?.stallType?.stallTypeNameTh)),
    },
    /** GET /announce/:id join zone มาให้ แต่ list-Bookmark-New ยังไม่ยืนยัน
     *  ไม่มีข้อมูลโซนก็ตัดช่องนี้ออก ไม่ต้องโชว์ขีด */
    ...(zones !== "-" ? [{ label: "โซน", value: zones }] : []),
    /** เลขแผง — การ์ดเดิมทั้งหน้าหลักและหน้ารายละเอียดแสดงไว้ ต้องคงไว้ */
    { label: "แผง", value: announceStallNames(announce) },
    dateItem,
  ];
};

export const fileUrl = (file: any) =>
  `${process.env.REACT_APP_FILE_URL}${file?.filePath || ""}${file?.fileName || ""}`;

const fileExt = (file: any) =>
  (file?.fileName || "").split(".").pop()?.toLowerCase() || "";

export const isPdf = (file: any) =>
  file?.mimetype === "application/pdf" || fileExt(file) === "pdf";

/**
 * แยกไฟล์แนบเป็น "รูปที่เอาไปทำสไลด์ได้" กับ "เอกสารที่ต้องขึ้นเป็นรายการให้กด"
 * ไม่ตัดสินจาก mimetype ตัวเดียว เพราะถ้า backend คืน null หรือชื่อ field อื่นมา
 * ไฟล์ pdf จะหลุดไปถูก render เป็น <img> แล้วรูปแตก และลิสต์ "ไฟล์แนบ" จะว่างเปล่า
 * อะไรที่ไม่ชัดว่าเป็นรูป ให้ตกไปอยู่ฝั่งเอกสารเสมอ
 */
const IMAGE_EXT = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];

export const isViewableImage = (file: any) => {
  if (!file?.fileName) return false;
  if (isPdf(file)) return false;
  if (typeof file?.mimetype === "string" && file.mimetype.startsWith("image/")) {
    return true;
  }
  return IMAGE_EXT.indexOf(fileExt(file)) !== -1;
};

export const splitAnnounceFiles = (fileUpload: any) => {
  const list = (Array.isArray(fileUpload) ? fileUpload : []).filter(
    (e: any) => e?.fileName
  );
  const images = list.filter((e: any) => isViewableImage(e));
  return { images, documents: list.filter((e: any) => !isViewableImage(e)) };
};
